import { Injectable } from '@angular/core';
import * as mountain from './styles/mountain';
import * as synthwave from './styles/synthwave';
import * as circuit from './styles/circuit';
import * as forest from './styles/forest';
import * as checkered from './styles/checkered';
import * as hexagon from './styles/hexagon';
import * as terrazzo from './styles/terrazzo';
import * as isometric from './styles/isometric';
import * as bauhaus from './styles/bauhaus';
import * as dunes from './styles/dunes';
import { DesignBox, Rgb, Scene, WallpaperParam, WallpaperStyle, WallpaperStyleInfo, darken, degrees, designBox, hexToRgb, luminance, mix, prng, rgba, share, vignette } from './wallpaper-core';

// the shared types and helpers live in wallpaper-core.ts (the style modules import them too); re-exported so the pages keep importing from here
export { WallpaperParam, WallpaperStyleInfo, WallpaperStyle, Scene, Rgb } from './wallpaper-core';

/**
 * The procedural wallpaper renderer behind /tools/wallpaper (2026-09-22).
 *
 * Everything is drawn on a 2D canvas from nothing but a palette (2-10 hex
 * colors), a style and an integer seed, so the same URL always renders the same
 * picture at any resolution: the preview is drawn small, the download is drawn
 * at the chosen preset's full size, and both come out identical because every
 * coordinate is a fraction of the canvas and every random number comes from the
 * seeded generator below.
 */


export const WALLPAPER_STYLES: WallpaperStyleInfo[] = [
  { key: 'mesh', label: 'Mesh', blurb: 'Soft blurred blobs of every color melting into each other, the macOS look',
    params: [ share( 'blobs', 'Blobs' ), share( 'softness', 'Softness' ) ] },
  { key: 'waves', label: 'Waves', blurb: 'Layered rolling waves stacked from the bottom, one color per crest',
    params: [ share( 'height', 'Height' ), share( 'frequency', 'Frequency' ) ] },
  { key: 'aurora', label: 'Aurora', blurb: 'Wavy ribbons of light rippling across a starry night sky',
    params: [ share( 'glow', 'Glow' ), share( 'spread', 'Spread' ) ] },
  { key: 'lowpoly', label: 'Low Poly', blurb: 'A faceted crystal of triangles shaded through the palette',
    params: [ share( 'density', 'Density' ), degrees( 'angle', 'Angle', 330 ) ] },
  { key: 'stripes', label: 'Stripes', blurb: 'Bold diagonal bands of color in varying widths',
    params: [ share( 'bands', 'Bands' ), degrees( 'angle', 'Angle', 0 ) ] },
  { key: 'orbs', label: 'Orbs', blurb: 'Translucent glass spheres floating over a deep gradient',
    params: [ share( 'count', 'Count' ), share( 'size', 'Size' ) ] },
];

// the styles that live in their own module (styles/<key>.ts): each exports INFO and draw, and is
// registered here so the list, the dropdown and the renderer pick it up from one place
const STYLE_MODULES: { INFO: WallpaperStyleInfo; draw: ( scene: Scene ) => void }[] = [
  mountain, synthwave, circuit, forest, checkered, hexagon, terrazzo, isometric, bauhaus, dunes,
];
STYLE_MODULES.forEach( m => WALLPAPER_STYLES.push( m.INFO ) );

export function styleInfoOf( style: string ): WallpaperStyleInfo {
  return WALLPAPER_STYLES.find( s => s.key === style ) || WALLPAPER_STYLES[ 0 ];
}

/** the two fields a style starts with */
export function defaultParams( style: string ): [ number, number ] {
  const info = styleInfoOf( style );
  return [ info.params[ 0 ].defaultValue, info.params[ 1 ].defaultValue ];
}

/** the two fields from the URL value "a-b" (clamped to each field's range), or the style's defaults */
export function parseParams( raw: string | null | undefined, style: string ): [ number, number ] {
  const info = styleInfoOf( style );
  const parts = String( raw || '' ).split( '-' ).map( v => parseInt( v, 10 ) );
  if ( parts.length !== 2 || parts.some( v => isNaN( v ) ) ) return defaultParams( style );
  return [ Math.max( 0, Math.min( info.params[ 0 ].max, parts[ 0 ] ) ), Math.max( 0, Math.min( info.params[ 1 ].max, parts[ 1 ] ) ) ];
}

export function paramsAreDefault( style: string, params: number[] ): boolean {
  const d = defaultParams( style );
  return params.length === 2 && params[ 0 ] === d[ 0 ] && params[ 1 ] === d[ 1 ];
}


export interface WallpaperSize {
  key: string;
  label: string;
  /** what the size is for ("iPhone", "4K monitor") */
  hint: string;
  width: number;
  height: number;
}

export const WALLPAPER_SIZES: WallpaperSize[] = [
  { key: 'fhd', label: '1920 × 1080', hint: 'Full HD desktop', width: 1920, height: 1080 },
  { key: 'qhd', label: '2560 × 1440', hint: 'QHD desktop', width: 2560, height: 1440 },
  { key: '4k', label: '3840 × 2160', hint: '4K desktop', width: 3840, height: 2160 },
  { key: '5k', label: '5120 × 2880', hint: '5K / iMac', width: 5120, height: 2880 },
  { key: 'macbook', label: '2880 × 1800', hint: 'MacBook Pro', width: 2880, height: 1800 },
  { key: 'ultrawide', label: '3440 × 1440', hint: 'Ultrawide 21:9', width: 3440, height: 1440 },
  { key: 'uwfhd', label: '2560 × 1080', hint: 'Ultrawide FHD 21:9', width: 2560, height: 1080 },
  { key: 'superultrawide', label: '5120 × 1440', hint: 'Super ultrawide 32:9', width: 5120, height: 1440 },
  { key: 'dualfhd', label: '3840 × 1080', hint: 'Super ultrawide FHD 32:9', width: 3840, height: 1080 },
  { key: 'iphone', label: '1290 × 2796', hint: 'iPhone', width: 1290, height: 2796 },
  { key: 'android', label: '1440 × 3200', hint: 'Android phone', width: 1440, height: 3200 },
  { key: 'ipad', label: '2048 × 2732', hint: 'iPad', width: 2048, height: 2732 },
  { key: 'square', label: '2048 × 2048', hint: 'Square / avatar', width: 2048, height: 2048 },
];

export interface WallpaperOptions {
  style: WallpaperStyle;
  /** 2-10 hex colors, '#rrggbb' */
  colors: string[];
  /** the art seed: same seed + same palette + same style = the same picture */
  seed: number;
  /** the style's two fields, 0..100 each (50 = default) */
  params?: number[];
}

@Injectable( {
  providedIn: 'root',
} )
export class WallpaperService {

  /** the style for a URL value, or the default */
  styleOf( value: string | null | undefined ): WallpaperStyle {
    const found = WALLPAPER_STYLES.find( s => s.key === ( value || '' ).toLowerCase() );
    return found ? found.key : 'mesh';
  }

  /** the size preset for a URL value, or the default */
  sizeOf( value: string | null | undefined ): WallpaperSize {
    return WALLPAPER_SIZES.find( s => s.key === ( value || '' ).toLowerCase() ) || WALLPAPER_SIZES[ 0 ];
  }

  /** a fresh art seed (six digits, easy to read in a URL) */
  randomSeed(): number {
    return 100000 + Math.floor( Math.random() * 900000 );
  }

  /**
   * Draws the wallpaper onto `canvas` at its current width × height. ONE design for every size
   * (owner, 2026-09-22): the picture is always composed at 16:9 and any other size is a centred
   * crop of it, scaled to cover. The picture is resolution independent: only fractions of the box are used, so 5K
   * equals Full HD.
   */
  render( canvas: HTMLCanvasElement, options: WallpaperOptions ): void {
    const ctx = canvas.getContext( '2d' );
    if ( !ctx ) return;
    const w = canvas.width;
    const h = canvas.height;
    // A canvas TALLER than 16:9 (a phone, a tablet, a square) is a centred crop of the 16:9 design
    // scaled to cover it (owner, 2026-09-22: "just crop"), so it shows the middle of the desktop
    // picture. A canvas WIDER than 16:9 (the ultrawides) is drawn at its own size instead: the
    // design box is as tall as the canvas and the procedures continue sideways, so nothing is cut
    // off the top or the bottom (owner, 2026-09-26: synthwave's sun was losing its head)
    if ( w / h < 16 / 9 - 0.01 ) {
      const master = document.createElement( 'canvas' );
      master.height = h;
      master.width = Math.round( h * 16 / 9 );
      this.render( master, options );
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      ctx.clearRect( 0, 0, w, h );
      ctx.drawImage( master, Math.round( ( master.width - w ) / 2 ), Math.round( ( master.height - h ) / 2 ), w, h, 0, 0, w, h );
      return;
    }
    const rand = prng( options.seed );
    // the palette in the tiles' order: the first color is the base (sky, ground), the rest follow in
    // order, in every style (owner, 2026-09-22); the seed drives the geometry only
    const colors = ( options.colors.length ? options.colors : [ '#00baff', '#ba00ff' ] ).map( hexToRgb );
    const info = styleInfoOf( options.style );
    const raw = options.params && options.params.length === 2 ? options.params : defaultParams( options.style );
    // each field as 0..1 of its range (a share out of 100, or a turn of 360°); the third slot is the procedure's fixed default
    const p: [ number, number, number ] = [ raw[ 0 ] / info.params[ 0 ].max, raw[ 1 ] / info.params[ 1 ].max, 0.5 ];
    const box = designBox( w, h );
    const scene: Scene = { ctx, w, h, box, colors, rand, p, seed: options.seed };
    ctx.save();
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect( 0, 0, w, h );

    const module = STYLE_MODULES.find( m => m.INFO.key === options.style );
    if ( module ) {
      module.draw( scene );
      ctx.restore();
      return;
    }

    switch ( options.style ) {
      case 'waves': this.waves( scene ); break;
      case 'aurora': this.aurora( scene ); break;
      case 'lowpoly': this.lowpoly( scene ); break;
      case 'stripes': this.stripes( scene ); break;
      case 'orbs': this.orbs( scene ); break;
      default: this.mesh( scene );
    }

    ctx.restore();
  }

  // ---------------------------------------------------------------- styles
  // Every coordinate below is a fraction of the design box (bx, by, bw, bh); backgrounds and
  // anything that must reach the edges use the full canvas (w, h).

  /**
   * Mesh: the colors as big soft radial blobs on the darkest tone, drawn on a small canvas and
   * scaled up so the browser's bilinear filter blurs them into one another for free (no blur
   * filter, so every browser draws it the same).
   */
  private mesh( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
    const small = document.createElement( 'canvas' );
    // softness: the smaller the working canvas, the blurrier the upscale (12 = crisp blobs, 40 = fog)
    const scale = ( 12 + p[ 1 ] * 28 ) * ( box.w / 1920 );
    small.width = Math.max( 16, Math.round( w / scale ) );
    small.height = Math.max( 16, Math.round( h / scale ) );
    const k = small.width / w;
    const sc = small.getContext( '2d' )!;
    sc.fillStyle = rgba( colors[ 0 ], 1 );
    sc.fillRect( 0, 0, small.width, small.height );
    const blobs = 3 + Math.round( p[ 0 ] * 12 ) + Math.floor( rand() * 3 );
    // The blobs of the design box come from the main stream, so the picture in the middle is the
    // same at every size; a canvas wider than the box gets extra blobs for its margins from a
    // generator of its own, which leaves that stream untouched (owner, 2026-09-26: an ultrawide
    // has to be MORE of the same picture, not another one)
    const side = prng( seed ^ 0x51de );
    const margin = Math.max( 0, w - box.w );
    const extra = Math.round( blobs * margin / Math.max( 1, box.w ) );
    for ( let i = 0; i < blobs + extra; i++ ) {
      const main = i < blobs;
      const r2 = main ? rand : side;
      const c = colors[ i % colors.length ];
      const t = r2() * ( main ? box.w : margin );
      const x = ( main ? box.x + t : ( t < box.x ? t : box.x + box.w + ( t - box.x ) ) ) * k;
      const y = r2() * h * k;
      const r = ( 0.35 + ( main ? rand() : side() ) * 0.5 ) * box.h * 1.15 * k;
      const g = sc.createRadialGradient( x, y, 0, x, y, r );
      g.addColorStop( 0, rgba( c, 0.95 ) );
      g.addColorStop( 0.55, rgba( c, 0.45 ) );
      g.addColorStop( 1, rgba( c, 0 ) );
      sc.fillStyle = g;
      sc.fillRect( 0, 0, small.width, small.height );
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // two passes: small → mid → full keeps the interpolation smooth at 5K
    const mid = document.createElement( 'canvas' );
    mid.width = Math.max( 64, Math.round( w / 6 ) );
    mid.height = Math.max( 64, Math.round( h / 6 ) );
    const mc = mid.getContext( '2d' )!;
    mc.imageSmoothingEnabled = true;
    mc.imageSmoothingQuality = 'high';
    mc.drawImage( small, 0, 0, mid.width, mid.height );
    ctx.drawImage( mid, 0, 0, w, h );
    vignette( ctx, box, w, h, p[ 2 ] * 0.6 );
  }

  /** Waves: the darkest color is the sky, then one rolling layer per color from the back to the front. */
  private waves( { ctx, w, h, box, colors, rand, p }: Scene ): void {
    // the tiles' order: the first tile is the sky, the waves follow it back to front (owner, 2026-09-22)
    // the sky: the tile's color with a faint 10% darkening at the top (owner, 2026-09-22)
    const sky = ctx.createLinearGradient( 0, 0, 0, h );
    sky.addColorStop( 0, rgba( darken( colors[ 0 ], 0.1 ), 1 ) );
    sky.addColorStop( 1, rgba( colors[ 0 ], 1 ) );
    ctx.fillStyle = sky;
    ctx.fillRect( 0, 0, w, h );
    const layers = colors.slice( 1 ).concat( colors.length === 1 ? [ colors[ 0 ] ] : [] );
    const n = layers.length;
    layers.forEach( ( c, i ) => {
      const t = ( i + 1 ) / ( n + 1 );
      const baseY = h * ( 0.32 + t * 0.55 );
      const amp = box.h * ( 0.02 + p[ 0 ] * 0.12 + rand() * 0.04 ) * ( 1 - t * 0.4 );
      const freq = 0.5 + p[ 1 ] * 3 + rand() * 0.8;
      const phase = rand() * Math.PI * 2;
      const freq2 = 2.6 + rand() * 2;
      const phase2 = rand() * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo( 0, h );
      const steps = 160;
      for ( let s = 0; s <= steps; s++ ) {
        const x = ( s / steps ) * w;
        // the wave's phase runs along the design box, so a wider canvas continues it, never squeezes it
        const u = ( x - box.x ) / box.w;
        const y = baseY + Math.sin( u * Math.PI * 2 * freq + phase ) * amp + Math.sin( u * Math.PI * 2 * freq2 + phase2 ) * amp * 0.35;
        ctx.lineTo( x, y );
      }
      ctx.lineTo( w, h );
      ctx.closePath();
      const g = ctx.createLinearGradient( 0, baseY - amp, 0, h );
      g.addColorStop( 0, rgba( c, 1 ) );
      g.addColorStop( 1, rgba( darken( c, p[ 2 ] * 0.7 ), 1 ) );
      ctx.fillStyle = g;
      ctx.fill();
    } );
  }

  /** Aurora: wavy ribbons of light rippling across a night sky, one per color, each a soft glow along a sine path. */
  private aurora( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
    // the tiles' order: the first tile is the night sky, the ribbons walk the rest in order
    const night = darken( colors[ 0 ], 0.72 );
    const sky = ctx.createLinearGradient( 0, 0, 0, h );
    sky.addColorStop( 0, rgba( darken( night, 0.3 ), 1 ) );
    sky.addColorStop( 1, rgba( night, 1 ) );
    ctx.fillStyle = sky;
    ctx.fillRect( 0, 0, w, h );

    // a faint field of stars over the whole canvas, from its own generator so the ribbons below
    // draw the same whatever the canvas size
    const starRand = prng( seed ^ 0x57a5 );
    const stars = Math.round( 320 * ( w * h ) / ( box.w * box.h ) );
    for ( let i = 0; i < stars; i++ ) {
      const x = starRand() * w;
      const y = starRand() * h * 0.85;
      const r = ( 0.4 + starRand() * 1.2 ) * ( box.w / 1920 );
      ctx.fillStyle = rgba( { r: 255, g: 255, b: 255 }, 0.25 + starRand() * 0.6 );
      ctx.beginPath();
      ctx.arc( x, y, Math.max( 0.5, r ), 0, Math.PI * 2 );
      ctx.fill();
    }

    // The ribbons are computed per pixel on an offscreen canvas and added to the sky as one
    // picture: drawing them as a run of thin strips left seams (a fine vertical banding the
    // owner called "tır tır"), a per-pixel glow has none.
    const ribbons = colors.slice( 1 );
    const list = ribbons.length ? ribbons : colors;
    const sw = Math.min( w, 960 );
    const sh = Math.round( sw * h / w );
    const sx = sw / w;
    const sy = sh / h;
    const bx = box.x * sx, bw = box.w * sx, by = box.y * sy, bh = box.h * sy;
    const white = { r: 255, g: 255, b: 255 };
    const specs = list.map( ( c, i ) => {
      // the ribbon's centre line: two sines along the box, a gentle drift, its own phase
      const t = ( i + 0.5 ) / list.length;
      const baseY = by + bh * ( 0.2 + t * 0.4 + ( rand() - 0.5 ) * 0.12 );
      const amp = bh * ( 0.05 + rand() * 0.09 );
      const f1 = 0.8 + rand() * 1.2;
      const f2 = 2.2 + rand() * 2.2;
      const ph1 = rand() * Math.PI * 2;
      const ph2 = rand() * Math.PI * 2;
      const tilt = ( rand() - 0.5 ) * bh * 0.25;
      // Spread: how tall the ribbon's glow is; Glow: how bright
      const height = bh * ( 0.1 + p[ 1 ] * 0.35 + rand() * 0.06 );
      const alpha = ( 0.06 + p[ 0 ] * 0.2 ) * 1.5;
      const lift = rand() * Math.PI * 2;
      const edge = mix( c, white, 0.35 );
      return { c, edge, baseY, amp, f1, f2, ph1, ph2, tilt, height, alpha, lift };
    } );
    const acc = new Float32Array( sw * sh * 3 );
    const colY = new Float32Array( sw );
    const colH = new Float32Array( sw );
    const colA = new Float32Array( sw );
    specs.forEach( sp => {
      for ( let x = 0; x < sw; x++ ) {
        const u = ( x + 0.5 - bx ) / bw;
        colY[ x ] = sp.baseY + sp.tilt * ( u - 0.5 ) + Math.sin( u * Math.PI * 2 * sp.f1 + sp.ph1 ) * sp.amp + Math.sin( u * Math.PI * 2 * sp.f2 + sp.ph2 ) * sp.amp * 0.35;
        // the ribbon breathes: brighter and taller in places along its length
        const breath = 0.55 + 0.45 * Math.sin( u * Math.PI * 2 * 1.7 + sp.lift );
        colH[ x ] = sp.height * ( 0.6 + 0.4 * breath );
        colA[ x ] = sp.alpha * breath;
      }
      for ( let x = 0; x < sw; x++ ) {
        const yc = colY[ x ], hh = colH[ x ], a = colA[ x ];
        const top = yc - hh, span = hh * 1.45;
        const y0 = Math.max( 0, Math.floor( top ) ), y1 = Math.min( sh - 1, Math.ceil( top + span ) );
        for ( let y = y0; y <= y1; y++ ) {
          // the same profile the strips had: clear at the top, the colour at 55%, a lighter
          // fringe at 85%, clear again just under the centre line
          const v = ( y + 0.5 - top ) / span;
          let k: number, col: Rgb;
          if ( v <= 0 || v >= 1 ) continue;
          if ( v < 0.55 ) { k = v / 0.55; col = sp.c; }
          else if ( v < 0.85 ) { const q = ( v - 0.55 ) / 0.3; k = 1 - q * 0.1; col = mix( sp.c, sp.edge, q ); }
          else { const q = ( v - 0.85 ) / 0.15; k = 0.9 * ( 1 - q ); col = sp.edge; }
          const ka = k * a;
          const o = ( y * sw + x ) * 3;
          acc[ o ] += col.r * ka;
          acc[ o + 1 ] += col.g * ka;
          acc[ o + 2 ] += col.b * ka;
        }
      }
    } );
    const small = document.createElement( 'canvas' );
    small.width = sw;
    small.height = sh;
    const sctx = small.getContext( '2d' )!;
    const img = sctx.createImageData( sw, sh );
    const data = img.data;
    for ( let i = 0, o = 0; i < sw * sh; i++, o += 3 ) {
      data[ i * 4 ] = Math.min( 255, acc[ o ] );
      data[ i * 4 + 1 ] = Math.min( 255, acc[ o + 1 ] );
      data[ i * 4 + 2 ] = Math.min( 255, acc[ o + 2 ] );
      data[ i * 4 + 3 ] = 255;
    }
    sctx.putImageData( img, 0, 0 );
    ctx.globalCompositeOperation = 'lighter';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage( small, 0, 0, w, h );
    ctx.globalCompositeOperation = 'source-over';
    // the ground: a soft dark horizon
    const ground = ctx.createLinearGradient( 0, h * 0.8, 0, h );
    ground.addColorStop( 0, rgba( night, 0 ) );
    ground.addColorStop( 1, rgba( darken( night, 0.5 ), 0.9 ) );
    ctx.fillStyle = ground;
    ctx.fillRect( 0, 0, w, h );
  }

  /**
   * Low poly: a jittered triangle grid, each face lit from one corner and tinted by its place in
   * the palette. Cells are sized from the design box and every cell's jitter and shade come from
   * its own (seed, row, column) generator, so a taller canvas simply has more rows of the same grid.
   */
  private lowpoly( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
    // at 0° the first tile is on the left and the last on the right (owner, 2026-09-22)
    const cols = 4 + Math.round( p[ 0 ] * 16 ) + Math.floor( rand() * 3 );
    const cell = box.w / cols;
    // the light comes from a seeded corner; the palette runs along the diagonal of the box
    const lightX = rand() < 0.5 ? box.x : box.x + box.w;
    const lightY = rand() < 0.5 ? box.y : box.y + box.h;
    // the palette runs along the Angle field (0..100 = a full turn); the seed still picks the light
    rand();
    const angle = p[ 1 ] * Math.PI * 2;
    const dx = Math.cos( angle );
    const dy = Math.sin( angle );
    const k = 0.8;
    // grid rows / columns counted from the box's top-left, extended to cover the canvas
    const c0 = Math.floor( ( 0 - box.x ) / cell ) - 1;
    const c1 = Math.ceil( ( w - box.x ) / cell ) + 1;
    const r0 = Math.floor( ( 0 - box.y ) / cell ) - 1;
    const r1 = Math.ceil( ( h - box.y ) / cell ) + 1;
    const pt = ( r: number, c: number ) => {
      const g = prng( ( seed * 31 + r * 1000003 + c * 7919 ) >>> 0 );
      return { x: box.x + c * cell + ( g() - 0.5 ) * cell * 0.8, y: box.y + r * cell + ( g() - 0.5 ) * cell * 0.8, split: g() < 0.5, noise: g() - 0.5 };
    };
    const tri = ( a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }, noise: number ) => {
      const mx = ( a.x + b.x + c.x ) / 3;
      const my = ( a.y + b.y + c.y ) / 3;
      const proj = ( ( ( mx - box.x ) / box.w - 0.5 ) * dx + ( ( my - box.y ) / box.h - 0.5 ) * dy ) + 0.5;
      const t = Math.max( 0, Math.min( 0.9999, proj ) ) * ( colors.length - 1 );
      const i = Math.floor( t );
      const base = colors.length > 1 ? mix( colors[ i ], colors[ i + 1 ], t - i ) : colors[ 0 ];
      const dist = Math.hypot( mx - lightX, my - lightY ) / Math.hypot( box.w, box.h );
      const shade = ( -0.28 + ( 1 - dist ) * 0.5 + noise * 0.22 ) * k;
      const col = shade >= 0 ? mix( base, { r: 255, g: 255, b: 255 }, shade * 0.5 ) : darken( base, -shade );
      ctx.fillStyle = rgba( col, 1 );
      ctx.strokeStyle = rgba( col, 1 );
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo( a.x, a.y );
      ctx.lineTo( b.x, b.y );
      ctx.lineTo( c.x, c.y );
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    };
    for ( let r = r0; r < r1; r++ ) {
      for ( let c = c0; c < c1; c++ ) {
        const p00 = pt( r, c ), p10 = pt( r, c + 1 ), p01 = pt( r + 1, c ), p11 = pt( r + 1, c + 1 );
        if ( p00.split ) {
          tri( p00, p10, p11, p00.noise );
          tri( p00, p11, p01, p10.noise );
        } else {
          tri( p00, p10, p01, p00.noise );
          tri( p10, p11, p01, p10.noise );
        }
      }
    }
  }

  /** Stripes: bands cycling through the palette, in widths that vary with the seed; at 0° they stand upright with the first tile at the left. */
  private stripes( { ctx, w, h, box, colors, rand, p }: Scene ): void {
    ctx.fillStyle = rgba( colors[ 0 ], 1 );
    ctx.fillRect( 0, 0, w, h );
    const angle = p[ 1 ] * Math.PI * 2;
    const diag = Math.hypot( box.w, box.h );
    const reach = diag * 3;
    const unit = diag / ( 4 + p[ 0 ] * 36 + rand() * 4 );
    const width = () => unit * ( 1 - p[ 2 ] * 0.7 + rand() * p[ 2 ] * 2.2 );
    const band = ( x: number, bw: number, c: Rgb ) => {
      const g = ctx.createLinearGradient( x, 0, x + bw, 0 );
      g.addColorStop( 0, rgba( c, 1 ) );
      g.addColorStop( 1, rgba( darken( c, 0.12 ), 1 ) );
      ctx.fillStyle = g;
      ctx.fillRect( x, -reach, bw + 1, reach * 2 );
    };
    ctx.translate( box.x + box.w / 2, box.y + box.h / 2 );
    ctx.rotate( angle );
    // from the box's left edge to the right: first tile first, then in order; the same sequence
    // runs on leftwards (in reverse) to cover whatever lies beyond the box on a wider canvas
    const left = -box.w / 2;
    let x = left;
    let i = 0;
    while ( x < reach ) {
      const bw = width();
      band( x, bw, colors[ i % colors.length ] );
      x += bw;
      i++;
    }
    x = left;
    i = colors.length - 1;
    while ( x > -reach ) {
      const bw = width();
      band( x - bw, bw, colors[ i % colors.length ] );
      x -= bw;
      i = ( i - 1 + colors.length ) % colors.length;
    }
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    vignette( ctx, box, w, h, 0.22 );
  }

  /** Orbs: glassy spheres of every color drifting over a deep two-tone gradient. */
  private orbs( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
    const bg = ctx.createLinearGradient( 0, 0, w, h );
    bg.addColorStop( 0, rgba( darken( colors[ 0 ], 0.35 ), 1 ) );
    bg.addColorStop( 1, rgba( colors[ Math.min( 1, colors.length - 1 ) ], 1 ) );
    ctx.fillStyle = bg;
    ctx.fillRect( 0, 0, w, h );
    const count = 2 + Math.round( p[ 0 ] * 20 ) + Math.floor( rand() * 3 );
    const unit = box.h;
    // the box's spheres come from the main stream; the margins of a wider canvas get their own,
    // so the middle of an ultrawide is the 16:9 picture exactly
    const side = prng( seed ^ 0x0165 );
    const margin = Math.max( 0, w - box.w );
    const extra = Math.round( count * margin / Math.max( 1, box.w ) );
    for ( let i = 0; i < count + extra; i++ ) {
      const main = i < count;
      const r2 = main ? rand : side;
      const c = colors[ ( i + 1 ) % colors.length ];
      const r = unit * ( 0.03 + p[ 1 ] * 0.22 + r2() * ( 0.04 + p[ 1 ] * 0.16 ) );
      const t = r2() * ( main ? box.w : margin );
      const x = main ? box.x + t : ( t < box.x ? t : box.x + box.w + ( t - box.x ) );
      const y = r2() * h;
      const g = ctx.createRadialGradient( x - r * 0.35, y - r * 0.35, r * 0.1, x, y, r );
      g.addColorStop( 0, rgba( mix( c, { r: 255, g: 255, b: 255 }, 0.25 + p[ 2 ] * 0.5 ), 0.95 ) );
      g.addColorStop( 0.5, rgba( c, 1 - p[ 2 ] * 0.45 ) );
      g.addColorStop( 1, rgba( darken( c, 0.4 ), 0.9 - p[ 2 ] * 0.5 ) );
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc( x, y, r, 0, Math.PI * 2 );
      ctx.fill();
      // a rim light
      ctx.strokeStyle = rgba( { r: 255, g: 255, b: 255 }, 0.18 );
      ctx.lineWidth = Math.max( 1, r * 0.025 );
      ctx.stroke();
    }
    vignette( ctx, box, w, h, 0.3 );
  }

  // ---------------------------------------------------------------- finish

}
